import { Temporal } from '@js-temporal/polyfill'

import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { getChannelName } from '~publish/helpers/channels'
import { getValueLedPreviewServices } from '~publish/helpers/valueLedEmptyStates'
import { generatePostingSlots } from '~publish/pages/Channel/QueueList/postSlotManager'
import type { ScheduleV2 } from '~publish/pages/Channel/QueueList/postSlotManager'

export const MAX_VISIBLE_QUEUE_SLOTS = 5
export const MAX_EXPANDED_QUEUE_SLOTS = 20

const PREVIEW_HOURS = [9, 13, 17]

export type ValueLedQueueChannel = {
  id: string
  name: string
  service: Service
  timezone: string
  isQueuePaused: boolean
  postingSchedule: ScheduleV2[]
}

export type ValueLedQueueSlot = {
  id: string
  date: string
  channelId: string | null
  channelName: string
  channelService: Service
}

function takeRepresentativeSlots(
  slotsByChannel: ValueLedQueueSlot[][],
  limit: number,
): ValueLedQueueSlot[] {
  const firstSlotPerChannel = slotsByChannel.flatMap((slots) =>
    slots.slice(0, 1),
  )
  const remainingSlots = slotsByChannel
    .flatMap((slots) => slots.slice(1))
    .toSorted((a, b) => a.date.localeCompare(b.date))
  const availableSpaces = Math.max(0, limit - firstSlotPerChannel.length)

  return [
    ...firstSlotPerChannel.slice(0, limit),
    ...remainingSlots.slice(0, availableSpaces),
  ].toSorted((a, b) => a.date.localeCompare(b.date))
}

export function buildConnectedQueueSlots({
  channels,
  limit,
}: {
  channels: ValueLedQueueChannel[]
  limit: number
}): ValueLedQueueSlot[] {
  if (channels.length === 0) return []

  const slotsByChannel = channels.map((channel) => {
    if (channel.isQueuePaused) return []

    return generatePostingSlots({
      postingSchedule: channel.postingSchedule,
      timezone: channel.timezone,
      numberOfSlots: limit,
    }).map((slot) => ({
      id: `slot:${channel.id}:${slot.date}`,
      date: slot.date,
      channelId: channel.id,
      channelName: channel.name,
      channelService: channel.service,
    }))
  })

  return takeRepresentativeSlots(slotsByChannel, limit)
}

export function buildPreviewQueueSlots({
  onboardingServices,
  timezone,
  limit,
  now = Date.now(),
}: {
  onboardingServices: Service[] | null
  timezone: string
  limit: number
  now?: number
}): ValueLedQueueSlot[] {
  const services = getValueLedPreviewServices(onboardingServices)
  const nowInTimezone =
    Temporal.Instant.fromEpochMilliseconds(now).toZonedDateTimeISO(timezone)

  const slotsByService = services.map((service, index) => {
    const hour = PREVIEW_HOURS[index] ?? PREVIEW_HOURS[0]
    let firstSlot = nowInTimezone.startOfDay().with({ hour })
    if (Temporal.ZonedDateTime.compare(firstSlot, nowInTimezone) <= 0) {
      firstSlot = firstSlot.add({ days: 1 })
    }

    return Array.from({ length: limit }, (_, dayIndex) => {
      const date = firstSlot.add({ days: dayIndex }).toInstant().toString()
      return {
        id: `preview:${service}:${date}`,
        date,
        channelId: null,
        channelName: getChannelName(service),
        channelService: service,
      }
    })
  })

  return takeRepresentativeSlots(slotsByService, limit)
}
